import { createContext, useContext, type ReactNode } from "react";
import { NoAccess } from "../components/States";
import { money as formatMoney } from "../lib/format";
import { TalentWorkspaceContext, useStaffWorkspace, type TalentWorkspace } from "./session";
import { t } from "../lib/sprache";

/**
 * Recht „Beträge sehen“ (can_view_amounts, 0057) für alle Seiten des Agentur-Portals (RECHTE-6): Honorare, Anteile,
 * Provisionssätze und Rechnungsbeträge erscheinen ohne das Recht als „•••“, der Rechnungsbereich ist dann gesperrt.
 * Ohne Arbeitsbereich (Einzeltests, Creator-Portal) gilt: sichtbar. Die Datenbank liefert die Werte weiter; das Recht wirkt
 * in der Oberfläche (docs/ROLLEN_RECHTE.md).
 */
export const BETRAG_VERDECKT = "•••";
// Rückfall, falls ein Test den Sitzungsbaustein nur teilweise nachbildet
function creatorContext() {
  try {
    return TalentWorkspaceContext ?? null;
  } catch {
    return null;
  }
}
const CreatorCtx = creatorContext() ?? createContext<TalentWorkspace | null>(null);
export const betraegeAus = () => t("Beträge sind für dein Konto ausgeblendet. Ein Admin vergibt das Recht „Beträge sehen“ unter Einstellungen · Team & Rechte.", "Amounts are hidden for your account. An admin grants the “View amounts” permission under Settings · Team & permissions.");

export function useBetraegeSichtbar(): boolean {
  // Im Creator-Portal gelten die Rechte des Teams nicht (Doppelrolle: eigene Beträge bleiben sichtbar)
  const creator = useContext(CreatorCtx);
  let ws: ReturnType<typeof useStaffWorkspace> | null = null;
  try {
    ws = useStaffWorkspace();
  } catch {
    ws = null;   // außerhalb eines Agentur-Arbeitsbereichs (Creator-Portal, öffentliche Seiten, Einzeltests)
  }
  return creator || !ws ? true : ws.can("view_amounts");
}

/** Formatierer, der ohne das Recht nur „•••“ liefert (gleiche Signatur wie das Original). */
export function verdeckt<F extends (...args: never[]) => string>(sichtbar: boolean, f: F): F {
  return sichtbar ? f : ((() => BETRAG_VERDECKT) as unknown as F);
}

/** `money` aus lib/format, verdeckt ohne das Recht. */
export function useGeld(): typeof formatMoney {
  return verdeckt(useBetraegeSichtbar(), formatMoney);
}

/** Bereich, der vor allem aus Beträgen besteht (Rechnungen, Mahnungen, Auszahlungen, Kontoauszug). */
export function NurMitBetraegen({ children, was = t("Dieser Bereich zeigt Rechnungsbeträge.", "This area shows invoice amounts.") }: { children: ReactNode; was?: string }) {
  if (!useBetraegeSichtbar()) return <NoAccess text={`${was} ${betraegeAus()}`} />;
  return <>{children}</>;
}
